import {
  LESSONS,
  getLessonById,
  type LessonDefinition,
  type LessonId,
} from '../data/lessons'

export type LessonStatus = 'locked' | 'available' | 'completed'

export interface LessonProgress {
  status: LessonStatus
  sessions: number
  attempts: number
  correct: number
  /** Best accuracy for one session, from 0 to 1. */
  bestAccuracy: number
  bestStreak: number
  totalDurationMs: number
  lastPracticedAt: string | null
  completedAt: string | null
}

export interface Progress {
  version: 1
  lessons: Record<LessonId, LessonProgress>
  totalSessions: number
  totalAttempts: number
  totalCorrect: number
  totalDurationMs: number
  updatedAt: string | null
}

export interface SessionResult {
  lessonId: LessonId
  attempts: number
  correct: number
  durationMs: number
  bestStreak?: number
  /** ISO 8601 is recommended. Kept optional so tests and offline callers remain deterministic. */
  completedAt?: string
}

function createLessonProgress(status: LessonStatus): LessonProgress {
  return {
    status,
    sessions: 0,
    attempts: 0,
    correct: 0,
    bestAccuracy: 0,
    bestStreak: 0,
    totalDurationMs: 0,
    lastPracticedAt: null,
    completedAt: null,
  }
}

export function createDefaultProgress(): Progress {
  const lessons = Object.fromEntries(
    LESSONS.map((lesson, index) => [
      lesson.id,
      createLessonProgress(index === 0 ? 'available' : 'locked'),
    ]),
  ) as Record<LessonId, LessonProgress>

  return {
    version: 1,
    lessons,
    totalSessions: 0,
    totalAttempts: 0,
    totalCorrect: 0,
    totalDurationMs: 0,
    updatedAt: null,
  }
}

export const DEFAULT_PROGRESS: Progress = createDefaultProgress()

export function getSessionAccuracy(result: Pick<SessionResult, 'attempts' | 'correct'>): number {
  return result.attempts > 0 ? result.correct / result.attempts : 0
}

export function isPassingSession(result: SessionResult): boolean {
  const criteria = getLessonById(result.lessonId).passCriteria

  return (
    result.attempts >= criteria.minAttempts &&
    result.correct >= 0 &&
    result.correct <= result.attempts &&
    getSessionAccuracy(result) >= criteria.minAccuracy
  )
}

function assertValidResult(result: SessionResult): void {
  const wholeNonNegativeFields: Array<[label: string, value: number]> = [
    ['attempts', result.attempts],
    ['correct', result.correct],
    ['durationMs', result.durationMs],
    ['bestStreak', result.bestStreak ?? 0],
  ]

  for (const [label, value] of wholeNonNegativeFields) {
    if (!Number.isFinite(value) || value < 0 || !Number.isInteger(value)) {
      throw new RangeError(`${label} must be a non-negative integer`)
    }
  }

  if (result.correct > result.attempts) {
    throw new RangeError('correct cannot be greater than attempts')
  }

  if ((result.bestStreak ?? 0) > result.correct) {
    throw new RangeError('bestStreak cannot be greater than correct')
  }
}

function nextLessonAfter(id: LessonId): LessonDefinition | null {
  const index = LESSONS.findIndex((lesson) => lesson.id === id)
  return LESSONS[index + 1] ?? null
}

/**
 * Returns a new progress value. The input is never mutated and no storage or clock
 * is accessed, which keeps persistence choices in the UI layer.
 */
export function applySessionResult(progress: Progress, result: SessionResult): Progress {
  assertValidResult(result)

  const previousLesson = progress.lessons[result.lessonId]
  if (previousLesson.status === 'locked') {
    throw new Error(`Cannot record a locked lesson: ${result.lessonId}`)
  }

  const accuracy = getSessionAccuracy(result)
  const passed = isPassingSession(result)
  const completedNow = passed && previousLesson.status !== 'completed'
  const timestamp = result.completedAt ?? null
  const updatedLesson: LessonProgress = {
    ...previousLesson,
    status: passed ? 'completed' : previousLesson.status,
    sessions: previousLesson.sessions + 1,
    attempts: previousLesson.attempts + result.attempts,
    correct: previousLesson.correct + result.correct,
    bestAccuracy: Math.max(previousLesson.bestAccuracy, accuracy),
    bestStreak: Math.max(previousLesson.bestStreak, result.bestStreak ?? 0),
    totalDurationMs: previousLesson.totalDurationMs + result.durationMs,
    lastPracticedAt: timestamp ?? previousLesson.lastPracticedAt,
    completedAt: completedNow ? timestamp : previousLesson.completedAt,
  }

  const lessons: Record<LessonId, LessonProgress> = {
    ...progress.lessons,
    [result.lessonId]: updatedLesson,
  }

  if (passed) {
    const nextLesson = nextLessonAfter(result.lessonId)
    if (nextLesson && lessons[nextLesson.id].status === 'locked') {
      lessons[nextLesson.id] = {
        ...lessons[nextLesson.id],
        status: 'available',
      }
    }
  }

  return {
    ...progress,
    lessons,
    totalSessions: progress.totalSessions + 1,
    totalAttempts: progress.totalAttempts + result.attempts,
    totalCorrect: progress.totalCorrect + result.correct,
    totalDurationMs: progress.totalDurationMs + result.durationMs,
    updatedAt: timestamp ?? progress.updatedAt,
  }
}

export function getLessonStatus(progress: Progress, id: LessonId): LessonStatus {
  return progress.lessons[id].status
}

/** The first available lesson, or null after every lesson has been completed. */
export function getNextLesson(progress: Progress): LessonDefinition | null {
  return LESSONS.find((lesson) => progress.lessons[lesson.id].status === 'available') ?? null
}

export function getOverallAccuracy(progress: Progress): number {
  return progress.totalAttempts > 0 ? progress.totalCorrect / progress.totalAttempts : 0
}

export function getCompletionRatio(progress: Progress): number {
  const completed = LESSONS.filter(
    (lesson) => progress.lessons[lesson.id].status === 'completed',
  ).length
  return completed / LESSONS.length
}
